/**
 * V2.0.84 — Winning-prompt marketplace on the Landing page.
 *
 * Public gallery of the highest-committed prompts from real users
 * (from the V2.0.82 chat outcome telemetry). Filterable by property
 * type. Each chip has a "Try this →" CTA that pushes the user
 * through the signup flow with the prompt pre-loaded into their
 * first design's chat (via the `?first_prompt=` query param the
 * Dashboard picks up on new-design creation).
 *
 * Falls back to a curated set on cold telemetry — the gallery
 * NEVER looks empty.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const PROPERTY_TYPES = ["all", "ranch", "colonial", "modern", "cabin"];

export default function PromptMarketplace() {
  const [prompts, setPrompts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const params = { limit: 12, hours: 720 };
    if (filter !== "all") params.property_type = filter;
    api
      .get("/prompt-marketplace", { params })
      .then((r) => setPrompts(r.data?.prompts || []))
      .catch(() => setPrompts([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const handleTry = (label) => {
    // V2.0.84 — Stash BEFORE navigate so an OAuth roundtrip (login →
    // provider → callback) doesn't lose the prompt. sessionStorage
    // survives same-origin redirects but not cross-origin OAuth bounces
    // — localStorage bridges both. Dashboard/Editor read either.
    try {
      localStorage.setItem("blueprintai.first_prompt", label);
      sessionStorage.setItem("blueprintai.first_prompt", label);
    } catch { /* private-browsing quotas */ }
    navigate(
      `/dashboard?first_prompt=${encodeURIComponent(label)}`,
    );
  };

  return (
    <section
      id="marketplace"
      className="border-b border-zinc-800 bg-zinc-950/50"
      data-testid="prompt-marketplace"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24">
        <div className="flex items-start gap-3 mb-6">
          <Sparkles className="w-6 h-6 text-brass-400 mt-1" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1">
              // Prompt marketplace
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-50 tracking-tight">
              Prompts that worked, from real architects.
            </h2>
            <p className="font-body text-base text-zinc-400 mt-4 max-w-2xl leading-relaxed">
              These are the prompts other designers have run this month that produced
              a committed design change. Click one to start a fresh project with it
              pre-loaded.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-6" role="tablist">
          {PROPERTY_TYPES.map((pt) => (
            <button
              key={pt}
              type="button"
              onClick={() => setFilter(pt)}
              role="tab"
              aria-selected={filter === pt}
              data-testid={`marketplace-filter-${pt}`}
              className={
                "px-3 py-1 text-[11px] font-mono uppercase tracking-widest border transition-colors " +
                (filter === pt
                  ? "border-brass-500 bg-brass-500/10 text-brass-200"
                  : "border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300")
              }
            >
              {pt}
            </button>
          ))}
        </div>

        {loading && prompts.length === 0 && (
          <div className="text-zinc-500 text-sm font-mono py-8">Loading prompts…</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {prompts.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleTry(p.label)}
              data-testid={`marketplace-prompt-${i}`}
              className="group text-left border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-brass-700/50 p-4 transition-colors flex flex-col gap-3"
            >
              <div className="text-zinc-100 text-sm leading-snug">&ldquo;{p.label}&rdquo;</div>
              <div className="flex items-center justify-between mt-auto">
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                  {p.count > 0 ? `× ${p.count} this month` : "curated starter"}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 group-hover:text-brass-300 flex items-center gap-1">
                  Try this <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
